<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <!-- Schema Markup -->
    <SchemaMarkup 
      type="Article"
      :data="articleSchema"
    />
    <SchemaMarkup 
      type="BreadcrumbList"
      :data="breadcrumbSchema"
    />
    
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Vue.js 3 Composition API</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Vue.js 3 Composition API: Best Practices và Advanced Patterns</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>08/01/2025</span>
          <span class="mx-2">•</span>
          <span>15 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Web Development</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>Tại sao Composition API là tương lai của Vue.js?</h2>
          <p>Composition API trong Vue.js 3 mang lại sự linh hoạt và khả năng tái sử dụng code tốt hơn so với Options API. Đây là cách tiếp cận hiện đại để xây dựng các ứng dụng Vue.js phức tạp và có thể maintain dễ dàng.</p>
          
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4">🎯 Lợi ích của Composition API</h3>
            <ul class="space-y-3">
              <li><strong>Better TypeScript support</strong> – Type inference tốt hơn</li>
              <li><strong>Code reusability</strong> – Dễ dàng tái sử dụng logic</li>
              <li><strong>Better organization</strong> – Nhóm logic liên quan với nhau</li>
              <li><strong>Performance</strong> – Tree-shaking tốt hơn</li>
            </ul>
          </div>

          <h2>1. Setup Composition API với TypeScript</h2>
          <p>Bắt đầu với setup cơ bản cho dự án Vue.js 3 với TypeScript và Composition API.</p>
          
          <h3>Project Structure:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>src/
├── components/
│   ├── UserProfile.vue
│   └── ProductList.vue
├── composables/
│   ├── useAuth.ts
│   ├── useApi.ts
│   └── useLocalStorage.ts
├── types/
│   ├── user.ts
│   └── api.ts
└── main.ts</code></pre>

          <h3>Basic Composition API Setup:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;script setup lang="ts"&gt;
import { ref, computed, onMounted, watch } from 'vue'
import type { User } from '@/types/user'

// Reactive state
const users = ref&lt;User[]&gt;([])
const loading = ref(false)
const error = ref&lt;string | null&gt;(null)

// Computed properties
const userCount = computed(() => users.value.length)
const hasUsers = computed(() => userCount.value > 0)

// Methods
const fetchUsers = async () => {
  loading.value = true
  error.value = null
  
  try {
    const response = await fetch('/api/users')
    users.value = await response.json()
  } catch (err) {
    error.value = 'Failed to fetch users'
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchUsers()
})

// Watchers
watch(users, (newUsers) => {
  console.log('Users updated:', newUsers.length)
}, { deep: true })
&lt;/script&gt;</code></pre>

          <h2>2. Custom Composables - Tái sử dụng Logic</h2>
          <p>Composables là cách tốt nhất để tái sử dụng logic trong Vue.js 3. Dưới đây là một số composables hữu ích:</p>
          
          <h3>useApi Composable:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// composables/useApi.ts
import { ref, type Ref } from 'vue'

export function useApi&lt;T&gt;(url: string) {
  const data: Ref&lt;T | null&gt; = ref(null)
  const loading = ref(false)
  const error = ref&lt;string | null&gt;(null)

  const execute = async (): Promise&lt;T | null&gt; => {
    loading.value = true
    error.value = null
    
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error('Network error')
      
      data.value = await response.json()
      return data.value
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      return null
    } finally {
      loading.value = false
    }
  }

  return {
    data: readonly(data),
    loading: readonly(loading),
    error: readonly(error),
    execute
  }
}</code></pre>

          <h3>useLocalStorage Composable:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// composables/useLocalStorage.ts
import { ref, watch } from 'vue'

export function useLocalStorage&lt;T&gt;(key: string, defaultValue: T) {
  const storedValue = localStorage.getItem(key)
  const value = ref&lt;T&gt;(
    storedValue ? JSON.parse(storedValue) : defaultValue
  )

  watch(value, (newValue) => {
    localStorage.setItem(key, JSON.stringify(newValue))
  }, { deep: true })

  return value
}</code></pre>

          <h2>3. Advanced Patterns với Composition API</h2>
          
          <h3>Dependency Injection Pattern:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// composables/useAuth.ts
import { inject, provide, ref } from 'vue'

const AuthSymbol = Symbol('auth')

export interface AuthContext {
  user: Ref&lt;User | null&gt;
  login: (credentials: LoginCredentials) => Promise&lt;void&gt;
  logout: () => void;
  isAuthenticated: ComputedRef&lt;boolean&gt;
}

export function provideAuth(): AuthContext {
  const user = ref&lt;User | null&gt;(null)
  
  const login = async (credentials: LoginCredentials) => {
    // Login logic
    user.value = await authService.login(credentials)
  }
  
  const logout = () => {
    user.value = null
  }
  
  const isAuthenticated = computed(() => !!user.value)
  
  const authContext: AuthContext = {
    user: readonly(user),
    login,
    logout,
    isAuthenticated
  }
  
  provide(AuthSymbol, authContext)
  return authContext
}

export function useAuth(): AuthContext {
  const auth = inject&lt;AuthContext&gt;(AuthSymbol)
  if (!auth) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return auth
}</code></pre>

          <h3>Reactive State Management:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// composables/useStore.ts
import { reactive, readonly } from 'vue'

interface State {
  users: User[]
  products: Product[]
  cart: CartItem[]
}

const state = reactive&lt;State&gt;({
  users: [],
  products: [],
  cart: []
})

export const useStore = () => {
  const addUser = (user: User) => {
    state.users.push(user)
  }
  
  const addToCart = (product: Product, quantity: number = 1) => {
    const existingItem = state.cart.find(item => item.product.id === product.id)
    if (existingItem) {
      existingItem.quantity += quantity
    } else {
      state.cart.push({ product, quantity })
    }
  }
  
  const removeFromCart = (productId: string) => {
    const index = state.cart.findIndex(item => item.product.id === productId)
    if (index > -1) {
      state.cart.splice(index, 1)
    }
  }
  
  return {
    state: readonly(state),
    addUser,
    addToCart,
    removeFromCart
  }
}</code></pre>

          <h2>4. Performance Optimization với Composition API</h2>
          
          <h3>Lazy Loading Components:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;script setup lang="ts"&gt;
import { defineAsyncComponent, ref } from 'vue'

// Lazy load heavy components
const HeavyComponent = defineAsyncComponent(() => 
  import('./HeavyComponent.vue')
)

const showHeavy = ref(false)

const loadHeavyComponent = () => {
  showHeavy.value = true
}
&lt;/script&gt;

&lt;template&gt;
  &lt;div&gt;
    &lt;button @click="loadHeavyComponent"&gt;
      Load Heavy Component
    &lt;/button&gt;
    
    &lt;Suspense v-if="showHeavy"&gt;
      &lt;HeavyComponent /&gt;
      &lt;template #fallback&gt;
        &lt;div&gt;Loading...&lt;/div&gt;
      &lt;/template&gt;
    &lt;/Suspense&gt;
  &lt;/div&gt;
&lt;/template&gt;</code></pre>

          <h3>Memoization với computed và watchEffect:</h3>
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;script setup lang="ts"&gt;
import { ref, computed, watchEffect } from 'vue'

const items = ref&lt;Item[]&gt;([])
const filter = ref('')

// Computed với caching tự động
const filteredItems = computed(() => {
  return items.value.filter(item => 
    item.name.toLowerCase().includes(filter.value.toLowerCase())
  )
})

// watchEffect cho side effects
watchEffect(() => {
  console.log(`Filtered ${filteredItems.value.length} items`)
})
&lt;/script&gt;</code></pre>

          <h2>5. Testing Composition API</h2>
          <p>Testing composables và components sử dụng Composition API:</p>
          
          <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// tests/composables/useApi.test.ts
import { describe, it, expect, vi } from 'vitest'
import { useApi } from '@/composables/useApi'

// Mock fetch
global.fetch = vi.fn()

describe('useApi', () => {
  it('should fetch data successfully', async () => {
    const mockData = { id: 1, name: 'Test' }
    vi.mocked(fetch).mockResolvedValueOnce({
      ok: true,
      json: () => Promise.resolve(mockData)
    } as Response)

    const { data, loading, error, execute } = useApi('/api/test')
    
    await execute()
    
    expect(data.value).toEqual(mockData)
    expect(loading.value).toBe(false)
    expect(error.value).toBeNull()
  })
})</code></pre>

          <h2>6. Best Practices và Common Pitfalls</h2>
          
          <div class="grid md:grid-cols-2 gap-6 my-8">
            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-semibold text-green-800 mb-2">✅ Best Practices</h3>
              <ul class="text-green-700 space-y-1 text-sm">
                <li>• Sử dụng TypeScript cho type safety</li>
                <li>• Tách logic thành composables</li>
                <li>• Sử dụng readonly cho external state</li>
                <li>• Implement proper error handling</li>
                <li>• Test composables độc lập</li>
              </ul>
            </div>
            <div class="bg-red-50 p-4 rounded-lg">
              <h3 class="font-semibold text-red-800 mb-2">❌ Common Pitfalls</h3>
              <ul class="text-red-700 space-y-1 text-sm">
                <li>• Không sử dụng readonly cho reactive state</li>
                <li>• Tạo quá nhiều refs thay vì reactive</li>
                <li>• Không cleanup side effects</li>
                <li>• Mixing Options API và Composition API</li>
                <li>• Không handle async operations đúng cách</li>
              </ul>
            </div>
          </div>

          <h2>Kết Luận</h2>
          <p>Composition API trong Vue.js 3 mang lại sự linh hoạt và khả năng tái sử dụng code tốt hơn. Với các patterns và best practices được chia sẻ ở trên, bạn có thể xây dựng các ứng dụng Vue.js hiện đại, có thể maintain và scale tốt.</p>
          
          <div class="bg-blue-50 p-4 rounded-lg mt-6">
            <p class="text-blue-800 font-semibold mb-2">💡 Pro Tip:</p>
            <p class="text-blue-700">Sử dụng Vue DevTools để debug và monitor reactive state trong Composition API. Điều này giúp bạn hiểu rõ hơn về data flow và performance.</p>
          </div>
        </div>
      </article>

      <!-- Related Articles -->
      <div class="mt-12 bg-white rounded-lg shadow-lg p-8">
        <h2 class="text-2xl font-bold text-blue-600 mb-6">Bài Viết Liên Quan</h2>
        <div class="grid md:grid-cols-2 gap-6">
          <div class="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <h3 class="font-semibold mb-2">CSS Grid vs Flexbox: Khi nào dùng gì?</h3>
            <p class="text-sm text-gray-600 mb-3">So sánh chi tiết và hướng dẫn sử dụng</p>
            <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Development</span>
          </div>
          <div class="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <h3 class="font-semibold mb-2">JavaScript Performance: Tối ưu code</h3>
            <p class="text-sm text-gray-600 mb-3">Cách viết JavaScript hiệu quả và nhanh</p>
            <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Development</span>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="mt-12 text-center">
        <router-link to="/contact">
          <button class="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 text-lg font-semibold">
            Cần Tư Vấn Vue.js Development?
          </button>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import SchemaMarkup from '../SchemaMarkup.vue'

// Article Schema
const articleSchema = {
  headline: 'Vue.js 3 Composition API: Best Practices và Advanced Patterns',
  description: 'Hướng dẫn chi tiết về Vue 3 Composition API với examples thực tế và best practices cho developers.',
  image: 'https://webservices.vn/images/vue3-composition-api.webp',
  author: {
    '@type': 'Organization',
    name: 'Web Services',
    url: 'https://webservices.vn'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Web Services',
    logo: {
      '@type': 'ImageObject',
      url: 'https://webservices.vn/favicon.webp'
    }
  },
  datePublished: '2025-01-08T00:00:00+07:00',
  dateModified: '2025-01-08T00:00:00+07:00',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': 'https://webservices.vn/blog/vue3-composition-api'
  },
  articleSection: 'Web Development',
  wordCount: 2500,
  timeRequired: 'PT15M',
  inLanguage: 'vi-VN',
  keywords: ['Vue.js', 'Composition API', 'JavaScript', 'Frontend', 'Development']
}

// Breadcrumb Schema
const breadcrumbSchema = {
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Trang chủ',
      item: 'https://webservices.vn'
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: 'https://webservices.vn/blog'
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Vue.js 3 Composition API'
    }
  ]
}
</script>

<style scoped>
.prose h2 {
  @apply text-2xl font-bold text-gray-900 mt-8 mb-4;
}

.prose h3 {
  @apply text-xl font-semibold text-gray-800 mt-6 mb-3;
}

.prose p {
  @apply text-gray-700 leading-relaxed mb-4;
}

.prose ul, .prose ol {
  @apply mb-4;
}

.prose li {
  @apply mb-2;
}
</style>
